// src/pages/admin/AdminPlatformOsPage.jsx
// Phase 7 — M7: Super-admin Platform OS command centre
// Tabs: Health Dashboard · Event Ledger · Feature Flags · Webhook Oversight

import { useEffect, useState, useCallback } from 'react'

const PINK = '#FF3D7F'
const ORG  = '#FF8500'
const DARK = '#1A1A2E'
const GREY = '#9CA3AF'
const GR   = '#F3F4F6'

const api = (path, opts = {}) =>
  fetch(`/api/v1${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    ...opts,
  }).then(r => r.json())

const TABS = [
  ['health',  '🩺 Health'],
  ['events',  '📋 Event Ledger'],
  ['flags',   '🚩 Feature Flags'],
  ['webhooks','🔗 Webhooks'],
]

// ── Sub-components ─────────────────────────────────────────────────────────

function ScoreRing({ score }) {
  const color = score >= 95 ? '#10B981' : score >= 80 ? '#F59E0B' : score >= 60 ? ORG : '#EF4444'
  return (
    <div style={{ position: 'relative', width: 100, height: 100 }}>
      <svg width="100" height="100" viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="40" fill="none" stroke="#F3F4F6" strokeWidth="10" />
        <circle cx="50" cy="50" r="40" fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${(score / 100) * 251.2} 251.2`}
          strokeLinecap="round" transform="rotate(-90 50 50)" />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontWeight: 900, fontSize: '1.3rem', color }}>{score}</span>
        <span style={{ fontSize: '0.62rem', color: GREY, marginTop: -2 }}>score</span>
      </div>
    </div>
  )
}

function ServiceBadge({ label, score }) {
  const color = score >= 95 ? '#10B981' : score >= 80 ? '#F59E0B' : '#EF4444'
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderRadius: 8, background: GR, marginBottom: 6 }}>
      <span style={{ fontSize: '0.83rem', color: DARK, fontWeight: 600, textTransform: 'capitalize' }}>{label}</span>
      <span style={{ fontWeight: 800, fontSize: '0.83rem', color }}>{score}%</span>
    </div>
  )
}

function AlertRow({ alert }) {
  const colors = { critical: '#FEE2E2|#991B1B', warning: '#FEF3C7|#92400E', info: '#EFF6FF|#1E40AF' }
  const [bg, fg] = (colors[alert.severity] || '#F3F4F6|#6B7280').split('|')
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: bg, marginBottom: 6 }}>
      <span style={{ fontWeight: 800, fontSize: '0.75rem', color: fg, textTransform: 'uppercase', flexShrink: 0 }}>{alert.severity}</span>
      <span style={{ fontSize: '0.83rem', color: DARK }}>{alert.message}</span>
      <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: GREY, flexShrink: 0 }}>{alert.service}</span>
    </div>
  )
}

function StatCard({ label, value, color = DARK, sub }) {
  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderTop: `3px solid ${color}` }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 900, color }}>{value}</div>
      <div style={{ fontWeight: 600, color: DARK, fontSize: '0.85rem', marginTop: 2 }}>{label}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: GREY, marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function FlagRow({ flag, onToggle, onRollout }) {
  const [rolloutInput, setRolloutInput] = useState(flag.rollout_percentage || 0)
  const effective = flag.effective_value
  return (
    <tr style={{ borderBottom: '1px solid #F9FAFB' }}>
      <td style={{ padding: '12px 14px' }}>
        <div style={{ fontWeight: 700, color: DARK, fontSize: '0.87rem', fontFamily: 'monospace' }}>{flag.flag_key}</div>
        <div style={{ fontSize: '0.75rem', color: GREY, marginTop: 2 }}>{flag.description}</div>
      </td>
      <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: GREY }}>{flag.flag_type}</td>
      <td style={{ padding: '12px 14px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <div onClick={() => onToggle(flag.flag_key, !flag.global_value)}
            style={{ width: 38, height: 20, borderRadius: 10, position: 'relative', background: flag.global_value ? PINK : '#D1D5DB', transition: 'background 0.2s', cursor: 'pointer' }}>
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: flag.global_value ? 20 : 2, transition: 'left 0.2s' }} />
          </div>
          <span style={{ fontSize: '0.8rem', color: flag.global_value ? PINK : GREY, fontWeight: 700 }}>{flag.global_value ? 'ON' : 'OFF'}</span>
        </label>
      </td>
      <td style={{ padding: '12px 14px' }}>
        {flag.flag_type === 'percentage' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="number" min="0" max="100" value={rolloutInput} onChange={e => setRolloutInput(+e.target.value)}
              style={{ width: 60, padding: '4px 8px', borderRadius: 7, border: '1.5px solid #E5E7EB', fontSize: '0.82rem' }} />
            <span style={{ fontSize: '0.78rem', color: GREY }}>%</span>
            <button onClick={() => onRollout(flag.flag_key, rolloutInput)}
              style={{ padding: '4px 8px', borderRadius: 7, border: 'none', background: ORG, color: '#fff', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Set</button>
          </div>
        ) : <span style={{ fontSize: '0.8rem', color: GREY }}>—</span>}
      </td>
      <td style={{ padding: '12px 14px', fontSize: '0.8rem', color: GREY }}>{flag.owner ?? '—'}</td>
      <td style={{ padding: '12px 14px' }}>
        <span style={{ background: effective ? '#DCFCE7' : GR, color: effective ? '#166534' : GREY, padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>
          {effective ? 'ENABLED' : 'DISABLED'}
        </span>
      </td>
    </tr>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function AdminPlatformOsPage() {
  const [tab, setTab]             = useState('health')
  const [dashboard, setDashboard] = useState(null)
  const [health, setHealth]       = useState(null)
  const [events, setEvents]       = useState([])
  const [flags, setFlags]         = useState([])
  const [webhooks, setWebhooks]   = useState([])
  const [loading, setLoading]     = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  // Filters
  const [evtTypeFilter, setEvtTypeFilter] = useState('')
  const [evtHours, setEvtHours]           = useState(24)
  const [wkStatus, setWkStatus]           = useState('')
  const [flagModal, setFlagModal]         = useState(false)
  const [flagForm, setFlagForm]           = useState({ flag_key: '', description: '', flag_type: 'boolean', global_value: false, rollout_percentage: 0, owner: '' })

  const load = useCallback(async () => {
    setRefreshing(true)
    try {
      const [dash, hlth, evts, flgs, wks] = await Promise.all([
        api('/admin/platform/dashboard'),
        api('/admin/platform/health'),
        api(`/admin/platform/events?hours=${evtHours}${evtTypeFilter ? `&type=${evtTypeFilter}` : ''}`),
        api('/admin/platform/feature-flags'),
        api('/admin/platform/webhooks'),
      ])
      setDashboard(dash.data ?? dash)
      setHealth(hlth.data ?? hlth)
      setEvents((evts.data?.data ?? []))
      setFlags(Array.isArray(flgs.data) ? flgs.data : [])
      setWebhooks((wks.data?.data ?? []))
    } finally {
      setLoading(false); setRefreshing(false)
    }
  }, [evtHours, evtTypeFilter])

  useEffect(() => { load() }, [load])

  // Auto-refresh health every 60s
  useEffect(() => {
    const t = setInterval(() => { if (tab === 'health') load() }, 60000)
    return () => clearInterval(t)
  }, [tab, load])

  const toggleFlag = async (key, value) => {
    await api(`/admin/platform/feature-flags/${key}/toggle`, { method: 'PATCH', body: JSON.stringify({ value }) })
    setFlags(prev => prev.map(f => f.flag_key === key ? { ...f, global_value: value, effective_value: value } : f))
  }

  const setRollout = async (key, pct) => {
    await api(`/admin/platform/feature-flags/${key}/rollout`, { method: 'PATCH', body: JSON.stringify({ percentage: pct }) })
    load()
  }

  const upsertFlag = async () => {
    await api('/admin/platform/feature-flags', { method: 'POST', body: JSON.stringify(flagForm) })
    setFlagModal(false); load()
  }

  const seedFlags = async () => {
    const r = await api('/admin/platform/feature-flags/seed', { method: 'POST' })
    alert((r.data?.message ?? r.message) || 'Seeded default flags')
    load()
  }

  const pauseWebhook = async (id) => {
    await api(`/admin/platform/webhooks/${id}/pause`, { method: 'POST' })
    load()
  }

  const resumeWebhook = async (id) => {
    await api(`/admin/platform/webhooks/${id}/resume`, { method: 'POST' })
    load()
  }

  const dispatchNow = async () => {
    const r = await api('/admin/webhooks/dispatch-now', { method: 'POST' })
    alert(r.data?.message ?? r.message ?? 'Dispatched')
    load()
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: GREY }}>Loading Platform OS…</div>

  const hl = health?.summary ?? {}
  const svc = hl.service_scores ?? {}
  const alerts = hl.alerts ?? []
  const m = hl.live_metrics ?? {}
  const dash = dashboard ?? {}

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 0 56px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '1.85rem', color: DARK, margin: 0 }}>Platform OS</h1>
          <p style={{ color: GREY, margin: '4px 0 0', fontSize: '0.88rem' }}>Real-time health · Event ledger · Feature flags · Webhook oversight</p>
        </div>
        <button onClick={load} disabled={refreshing}
          style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: refreshing ? GREY : DARK, fontWeight: 700, cursor: refreshing ? 'default' : 'pointer', fontSize: '0.83rem' }}>
          {refreshing ? '↻ Refreshing…' : '↻ Refresh'}
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, background: GR, borderRadius: 12, padding: 4, width: 'fit-content', marginBottom: 24 }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} style={{ padding: '8px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem', background: tab === k ? '#fff' : 'transparent', color: tab === k ? PINK : '#6B7280', boxShadow: tab === k ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
            {l}
          </button>
        ))}
      </div>

      {/* ── HEALTH ── */}
      {tab === 'health' && (
        <div>
          {/* Top KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 24 }}>
            <StatCard label="Total Endpoints"     value={dash.webhook_summary?.total_endpoints ?? 0} color={DARK} />
            <StatCard label="Pending Deliveries"  value={dash.webhook_summary?.pending_deliveries ?? 0} color={ORG} />
            <StatCard label="Active Users (1h)"   value={m.active_users_1h ?? 0} color={PINK} />
            <StatCard label="Orders Last Hour"    value={m.orders_last_hour ?? 0} color="#10B981" />
            <StatCard label="Queue Depth"         value={m.queue_depth ?? 0} color={m.queue_depth > 100 ? '#EF4444' : DARK} />
          </div>

          {/* Score + services */}
          <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 20, marginBottom: 24 }}>
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
              <ScoreRing score={Math.round(hl.overall_score ?? 0)} />
              <div style={{ fontWeight: 800, fontSize: '1rem', color: DARK }}>{hl.health_label ?? 'Unknown'}</div>
              <div style={{ fontSize: '0.78rem', color: GREY, textAlign: 'center' }}>Last captured: {hl.captured_at ? new Date(hl.captured_at).toLocaleTimeString('en-IN') : '—'}</div>
            </div>
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
              <div style={{ fontWeight: 700, color: DARK, marginBottom: 14, fontSize: '0.9rem' }}>Service Scores</div>
              {Object.entries(svc).map(([s, v]) => <ServiceBadge key={s} label={s} score={Math.round(v)} />)}
              {Object.keys(svc).length === 0 && <div style={{ color: GREY, fontSize: '0.85rem' }}>No service scores yet — capture health snapshots to populate.</div>}
            </div>
          </div>

          {/* Alerts */}
          {alerts.length > 0 && (
            <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', marginBottom: 24 }}>
              <div style={{ fontWeight: 700, color: DARK, marginBottom: 14, fontSize: '0.9rem' }}>⚠️ Active Alerts</div>
              {alerts.map((a, i) => <AlertRow key={i} alert={a} />)}
            </div>
          )}

          {/* Live metrics grid */}
          <div style={{ background: '#fff', borderRadius: 18, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <div style={{ fontWeight: 700, color: DARK, marginBottom: 14, fontSize: '0.9rem' }}>Live Metrics</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              {[
                ['Orders Today',        m.orders_today],
                ['GMV Today (₹)',       '₹'+(parseFloat(m.gmv_today??0)/100000).toFixed(1)+'L'],
                ['Total Suppliers',     m.total_suppliers],
                ['Total Buyers',        m.total_buyers],
                ['New Users Today',     m.new_users_today],
                ['Payment Fail Rate',   (parseFloat(m.payment_fail_rate??0)*100).toFixed(1)+'%'],
                ['Failed Jobs',         m.failed_jobs],
                ['Pending Webhooks',    m.pending_webhooks],
                ['Avg Response (ms)',   m.avg_response_ms],
              ].map(([l, v]) => (
                <div key={l} style={{ padding: '10px 14px', background: GR, borderRadius: 10 }}>
                  <div style={{ fontSize: '0.75rem', color: GREY, marginBottom: 2 }}>{l}</div>
                  <div style={{ fontWeight: 800, color: DARK, fontSize: '1rem' }}>{v ?? '—'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── EVENTS ── */}
      {tab === 'events' && (
        <div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
            <input placeholder="Filter by event type…" value={evtTypeFilter} onChange={e => setEvtTypeFilter(e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: '0.84rem', width: 240 }} />
            <select value={evtHours} onChange={e => setEvtHours(+e.target.value)}
              style={{ padding: '8px 12px', borderRadius: 10, border: '1.5px solid #E5E7EB', fontSize: '0.84rem' }}>
              {[1, 6, 24, 48, 168].map(h => <option key={h} value={h}>Last {h}h</option>)}
            </select>
            <button onClick={load} style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}>Search</button>
          </div>

          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR }}>
                  {['Event Type','Aggregate','Tenant','Actor','Occurred At'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && <tr><td colSpan={5} style={{ padding: 32, textAlign: 'center', color: GREY }}>No events found for this filter.</td></tr>}
                {events.slice(0, 100).map((e, i) => (
                  <tr key={e.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: '#EDE9FE', color: '#5B21B6', padding: '2px 9px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace' }}>{e.event_type}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: '0.82rem', color: '#374151' }}>{e.aggregate_type ? `${e.aggregate_type} #${e.aggregate_id}` : '—'}</td>
                    <td style={{ padding: '10px 14px', fontSize: '0.82rem', color: GREY }}>{e.tenant_id ?? 'Platform'}</td>
                    <td style={{ padding: '10px 14px', fontSize: '0.82rem', color: GREY }}>{e.actor_type}{e.actor_id ? ` #${e.actor_id}` : ''}</td>
                    <td style={{ padding: '10px 14px', fontSize: '0.8rem', color: GREY, whiteSpace: 'nowrap' }}>{e.occurred_at ? new Date(e.occurred_at).toLocaleString('en-IN') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── FEATURE FLAGS ── */}
      {tab === 'flags' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'flex-end' }}>
            <button onClick={seedFlags} style={{ padding: '8px 16px', borderRadius: 10, border: `1.5px solid ${ORG}`, background: '#fff', color: ORG, fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}>🌱 Seed Defaults</button>
            <button onClick={() => setFlagModal(true)} style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}>+ New Flag</button>
          </div>

          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR }}>
                  {['Flag Key','Type','Global Value','Rollout %','Owner','Effective'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {flags.length === 0 && <tr><td colSpan={6} style={{ padding: 32, textAlign: 'center', color: GREY }}>No feature flags. Click "Seed Defaults" to populate.</td></tr>}
                {flags.map(f => <FlagRow key={f.flag_key} flag={f} onToggle={toggleFlag} onRollout={setRollout} />)}
              </tbody>
            </table>
          </div>

          {/* Add Flag Modal */}
          {flagModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: 480, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>
                <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.2rem', fontWeight: 800, color: DARK, margin: '0 0 20px' }}>New Feature Flag</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[['flag_key','Flag Key (e.g. ai_search_v2)','text'],['description','Description','text'],['owner','Owner Team','text']].map(([k, l, t]) => (
                    <div key={k}>
                      <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: GREY, marginBottom: 4 }}>{l}</label>
                      <input type={t} value={flagForm[k]} onChange={e => setFlagForm(f => ({...f, [k]: e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem', boxSizing: 'border-box' }} />
                    </div>
                  ))}
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: GREY, marginBottom: 4 }}>Flag Type</label>
                    <select value={flagForm.flag_type} onChange={e => setFlagForm(f => ({...f, flag_type: e.target.value}))} style={{ width: '100%', padding: '9px 12px', borderRadius: 9, border: '1.5px solid #E5E7EB', fontSize: '0.87rem' }}>
                      {['boolean','percentage','tenant','user'].map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={flagForm.global_value} onChange={e => setFlagForm(f => ({...f, global_value: e.target.checked}))} style={{ accentColor: PINK, width: 16, height: 16 }} />
                    <span style={{ fontSize: '0.87rem', color: DARK, fontWeight: 600 }}>Enabled globally by default</span>
                  </label>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
                  <button onClick={() => setFlagModal(false)} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={upsertFlag} style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: PINK, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Create Flag</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── WEBHOOKS ── */}
      {tab === 'webhooks' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 8 }}>
              {['', 'active', 'paused', 'disabled'].map(s => (
                <button key={s} onClick={() => setWkStatus(s)} style={{ padding: '6px 14px', borderRadius: 10, border: `1.5px solid ${wkStatus === s ? PINK : '#E5E7EB'}`, background: wkStatus === s ? PINK : '#fff', color: wkStatus === s ? '#fff' : '#6B7280', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
                  {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
            <button onClick={dispatchNow} style={{ padding: '8px 18px', borderRadius: 10, border: 'none', background: ORG, color: '#fff', fontWeight: 700, cursor: 'pointer', fontSize: '0.83rem' }}>
              ⚡ Dispatch Now
            </button>
          </div>

          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 20 }}>
            {[
              ['Total Endpoints', dash.webhook_summary?.total_endpoints ?? 0, DARK],
              ['Active',          dash.webhook_summary?.active_endpoints ?? 0, '#10B981'],
              ['Pending Deliveries', dash.webhook_summary?.pending_deliveries ?? 0, ORG],
              ['Failed',          dash.webhook_summary?.failed_deliveries ?? 0, '#EF4444'],
            ].map(([l, v, c]) => <StatCard key={l} label={l} value={v} color={c} />)}
          </div>

          <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: GR }}>
                  {['Endpoint','Tenant','Event Types','Status','Success','Failures','Last Success','Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: GREY, textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {webhooks.filter(w => !wkStatus || w.status === wkStatus).length === 0 && (
                  <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: GREY }}>No webhook endpoints found.</td></tr>
                )}
                {webhooks.filter(w => !wkStatus || w.status === wkStatus).map((w, i) => (
                  <tr key={w.id} style={{ borderBottom: '1px solid #F9FAFB', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                    <td style={{ padding: '12px 14px', maxWidth: 200 }}>
                      <div style={{ fontWeight: 700, color: DARK, fontSize: '0.84rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{w.url}</div>
                      {w.description && <div style={{ fontSize: '0.75rem', color: GREY }}>{w.description}</div>}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: '0.83rem', color: GREY }}>#{w.tenant_id}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.78rem', color: GREY, maxWidth: 150 }}>{(w.event_types ?? []).join(', ')}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ background: w.status === 'active' ? '#DCFCE7' : w.status === 'paused' ? '#FEF3C7' : '#FEE2E2', color: w.status === 'active' ? '#166534' : w.status === 'paused' ? '#92400E' : '#991B1B', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700 }}>{w.status}</span>
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: '#10B981', fontSize: '0.84rem' }}>{w.success_count}</td>
                    <td style={{ padding: '12px 14px', fontWeight: 700, color: w.failure_count > 0 ? '#EF4444' : GREY, fontSize: '0.84rem' }}>{w.failure_count}</td>
                    <td style={{ padding: '12px 14px', fontSize: '0.78rem', color: GREY, whiteSpace: 'nowrap' }}>
                      {w.last_success_at ? new Date(w.last_success_at).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {w.status === 'active' && (
                          <button onClick={() => pauseWebhook(w.id)} style={{ padding: '4px 9px', borderRadius: 7, border: `1.5px solid ${ORG}`, background: '#fff', color: ORG, fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Pause</button>
                        )}
                        {w.status === 'paused' && (
                          <button onClick={() => resumeWebhook(w.id)} style={{ padding: '4px 9px', borderRadius: 7, border: 'none', background: '#10B981', color: '#fff', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Resume</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
